/**
 * InfinitSales - Message Media Components
 * Renders different media types in chat messages (WhatsApp-style)
 * 
 * Supports:
 * - Images: Thumbnail preview + fullscreen lightbox
 * - Audio: Waveform player with play/pause
 * - Video: Thumbnail + play overlay
 * - Documents: Icon + filename + download
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Image as ImageIcon,
  FileText,
  Video,
  Music,
  Play,
  Pause,
  Download,
  X,
  Loader2,
  File,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileText as FileTextIcon,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import transcriptionService from '../services/global/transcriptionService';
import TranscriptionLimitModal from './TranscriptionLimitModal';
import { saveBase64File, saveUrlFile } from '../utils/downloadFile';

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  
  // Handle object format from WhatsApp { low: number, high: number }
  const size = typeof bytes === 'object' ? bytes.low || 0 : bytes;
  
  if (size === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return parseFloat((size / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Format duration in seconds to mm:ss
 */
const formatDuration = (seconds) => {
  // `Infinity` e verdadeiro, entao `!seconds` nao o pega: sem esta guarda o
  // rodape do audio renderizava o literal "Infinity:NaN" (Math.floor(Inf/60)
  // = Infinity e Math.floor(Inf%60) = NaN). Acontece sempre que o navegador
  // nao sabe a duracao, que e o caso comum do OGG do WhatsApp servido sem
  // Content-Length. O WABusinessMedia (usado por Business, Instagram e
  // Messenger) ja tinha a guarda; so o wa_personal estava sem ela.
  if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get document icon based on mimetype
 */
const getDocumentIcon = (mimetype, fileName) => {
  const mime = (mimetype || '').toLowerCase();
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';
  
  if (mime.includes('pdf') || ext === 'pdf') {
    return { icon: FileText, color: '#E53935', label: 'PDF' };
  }
  if (mime.includes('word') || ['doc', 'docx'].includes(ext)) {
    return { icon: FileText, color: '#2196F3', label: 'DOC' };
  }
  if (mime.includes('excel') || mime.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) {
    return { icon: FileText, color: '#4CAF50', label: 'XLS' };
  }
  if (mime.includes('powerpoint') || mime.includes('presentation') || ['ppt', 'pptx'].includes(ext)) {
    return { icon: FileText, color: '#FF9800', label: 'PPT' };
  }
  if (mime.includes('zip') || mime.includes('rar') || ['zip', 'rar', '7z'].includes(ext)) {
    return { icon: File, color: '#9C27B0', label: 'ZIP' };
  }
  if (mime.includes('text') || ext === 'txt') {
    return { icon: FileText, color: '#607D8B', label: 'TXT' };
  }
  
  return { icon: File, color: '#757575', label: 'FILE' };
};

// ============================================
// IMAGE MESSAGE COMPONENT
// ============================================

export const ImageMessage = ({
  message,
  isSent,
  onOpenLightbox,
  onDownload,
}) => {
  const { colors } = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);
  // SAS do blob expira em 60 dias / blob pode faltar: com onError caimos no
  // thumbnail NITIDO em vez do glifo de imagem quebrada.
  const [failedSrc, setFailedSrc] = useState(null);
  const imageMessage = message.message?.imageMessage;

  if (!imageMessage) return null;
  
  const thumbnail = imageMessage.jpegThumbnail;
  // URL do blob (com token SAS, embutivel direto num <img>). Preferimos exibir a
  // imagem CHEIA inline, estilo WhatsApp Web, em vez de so o thumbnail/placeholder
  // que exigia clique. Cai pro thumbnail/placeholder se a URL nao existir.
  const fullUrl = imageMessage.url || message.mediaUrl;
  // URL crua da Evolution (mmg.whatsapp.net / .enc) e payload CRIPTOGRAFADO —
  // jamais renderiza num <img>; tratavamos como imagem e o browser mostrava o
  // glifo quebrado + alt por cima do thumbnail borrado (o "preview bugado").
  const embeddableUrl = (fullUrl && !/mmg\.whatsapp\.net|\.enc(\?|$)/i.test(fullUrl))
    ? fullUrl
    : null;
  // Erro é preso À URL que falhou: se a mensagem receber uma URL nova (ou a
  // linha for reciclada pela lista virtualizada), tenta de novo em vez de
  // ficar no thumbnail para sempre.
  const imgError = !!embeddableUrl && failedSrc === embeddableUrl;
  const caption = imageMessage.caption;
  const width = imageMessage.width || 300;
  const height = imageMessage.height || 200;
  
  // Calculate aspect ratio for display
  const aspectRatio = width / height;
  const displayWidth = Math.min(280, width);
  const displayHeight = displayWidth / aspectRatio;
  
  const styles = {
    container: {
      position: 'relative',
      borderRadius: 8,
      overflow: 'hidden',
      cursor: 'pointer',
      maxWidth: 280,
    },
    image: {
      width: displayWidth,
      height: Math.min(displayHeight, 320),
      objectFit: 'cover',
      display: 'block',
      backgroundColor: colors.bgTertiary,
    },
    placeholder: {
      width: displayWidth,
      height: Math.min(displayHeight, 200),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.4))',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      gap: 8,
      padding: 8,
      opacity: 0,
      transition: 'opacity 0.2s ease',
    },
    zoomIcon: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    downloadIcon: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      cursor: 'pointer',
      padding: 0,
    },
    caption: {
      padding: '8px 0 0',
      fontSize: 14,
      color: isSent ? colors.messageSentText : colors.messageReceivedText,
      wordBreak: 'break-word',
    },
  };

  const handleClick = () => {
    if (onOpenLightbox) {
      onOpenLightbox({
        type: 'image',
        thumbnail,
        // URL cheia embutivel: o lightbox exibe DIRETO (sem re-baixar pela
        // Evolution — que falhava e caia no thumbnail minusculo).
        url: (embeddableUrl && !imgError) ? embeddableUrl : null,
        messageKey: message.key,
        caption,
      });
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      if (onDownload) {
        const result = await onDownload(message.key, 'image');
        if (result?.base64) {
          // Cross-plataforma (iOS/PWA não honram <a download>).
          await saveBase64File(
            result.base64,
            result.mimetype || 'image/jpeg',
            result.fileName || `imagem_${Date.now()}.jpg`
          );
        }
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div 
        style={styles.container}
        onClick={handleClick}
        onMouseEnter={(e) => {
          const overlay = e.currentTarget.querySelector('.image-overlay');
          if (overlay) overlay.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          const overlay = e.currentTarget.querySelector('.image-overlay');
          if (overlay) overlay.style.opacity = '0';
        }}
      >
        {(embeddableUrl && !imgError) ? (
          <img
            src={embeddableUrl}
            alt=""
            loading="lazy"
            onError={() => setFailedSrc(embeddableUrl)}
            style={{
              ...styles.image,
              // Blur-up: mostra o thumbnail instantaneo atras enquanto a imagem
              // cheia (SAS) carrega.
              ...(thumbnail
                ? {
                    backgroundImage: `url(data:image/jpeg;base64,${thumbnail})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                : {}),
            }}
          />
        ) : thumbnail ? (
          // Original indisponivel (SAS vencido, blob ausente, URL .enc):
          // thumbnail NITIDO + selo discreto — nunca o glifo quebrado com a
          // legenda por cima.
          <div style={{ position: 'relative' }}>
            <img
              src={`data:image/jpeg;base64,${thumbnail}`}
              alt=""
              style={styles.image}
            />
            <span style={{
              position: 'absolute', bottom: 6, left: 6,
              padding: '2px 8px', borderRadius: 8, fontSize: 10,
              backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff',
            }}>
              Visualização
            </span>
          </div>
        ) : (
          <div style={styles.placeholder}>
            <ImageIcon size={48} />
          </div>
        )}
        <div className="image-overlay" style={styles.overlay}>
          <button
            style={styles.downloadIcon}
            onClick={handleDownload}
            disabled={isDownloading}
            title="Baixar"
          >
            {isDownloading ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Download size={16} />
            )}
          </button>
          <div style={styles.zoomIcon}>
            <ZoomIn size={18} />
          </div>
        </div>
      </div>
      {caption && <p style={styles.caption}>{caption}</p>}
    </div>
  );
};

// ============================================
// AUDIO MESSAGE COMPONENT
// ============================================

export const AudioMessage = ({
  message,
  isSent,
  onDownload,
  channel = 'wa-personal',
}) => {
  const { theme, colors } = useTheme();
  // Balão ENVIADO no tema claro é #d9fdd3 — branco-alpha fica invisível.
  // Branco só no escuro (balão #005c4b); no claro usa o verde da plataforma.
  const isDark = theme === 'dark';
  const sentOnDark = isSent && isDark;
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showTranscription, setShowTranscription] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [error, setError] = useState(null);
  const [transcription, setTranscription] = useState(message?.transcription || '');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const audioRef = useRef(null);
  // Marca que o audio deve tocar assim que o <audio> montar com o src recem
  // baixado — corrige o bug dos "2 cliques" (1o clique so baixava e retornava).
  const autoPlayRef = useRef(false);

  const audioMessage = message?.message?.audioMessage;
  const isPTT = audioMessage?.ptt; // Voice note (push-to-talk)
  const waveformData = audioMessage?.waveform;
  const audioDuration = audioMessage?.seconds || 0;
  
  // Parse waveform data (base64 encoded) - useMemo to avoid recalculating
  const waves = useMemo(() => {
    if (!waveformData) {
      // Generate fake waveform
      return Array(32).fill(0).map(() => Math.random() * 0.8 + 0.2);
    }
    try {
      const decoded = atob(waveformData);
      const values = [];
      for (let i = 0; i < decoded.length; i++) {
        values.push(decoded.charCodeAt(i) / 255);
      }
      return values.length > 0 ? values : Array(32).fill(0.5);
    } catch {
      return Array(32).fill(0.5);
    }
  }, [waveformData]);
  
  const progress = duration > 0 ? currentTime / duration : 0;

  // Audio element event listeners
  useEffect(() => {
    if (audioRef.current && audioSrc) {
      const audio = audioRef.current;
      
      const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
      const handleLoadedMetadata = () => setDuration(audio.duration);
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);

      // Auto-play assim que o src (data URL, sem rede) acabou de ser carregado
      // pelo proprio clique no play. Sem isto o usuario precisava clicar 2x.
      if (autoPlayRef.current) {
        autoPlayRef.current = false;
        audio.play().then(() => setIsPlaying(true)).catch(() => {});
      }

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [audioSrc]);

  // Sync when parent-provided transcription changes (e.g. after reload)
  useEffect(() => {
    if (message?.transcription && message.transcription !== transcription) {
      setTranscription(message.transcription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.transcription]);

  // Velocidade de reproducao (1x -> 1.5x -> 2x). Substitui o antigo botao de
  // baixar (baixar audio nao faz sentido no inbox). Aplica ao <audio> no mount
  // do src e sempre que muda.
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [audioSrc, playbackRate]);

  const SPEED_STEPS = [1, 1.5, 2];
  // Updater funcional p/ ser à prova de cliques rápidos; o useEffect acima
  // aplica o playbackRate ao <audio>.
  const cycleSpeed = () =>
    setPlaybackRate((r) => SPEED_STEPS[(SPEED_STEPS.indexOf(r) + 1) % SPEED_STEPS.length] || 1);

  const handleTranscribe = async () => {
    if (isTranscribing || transcription) return;
    setIsTranscribing(true);
    setTranscriptionError(null);
    try {
      const result = await transcriptionService.transcribeMessage({
        channel,
        messageId: message?.key?.id,
        chatId: message?.key?.remoteJid,
      });
      if (result?.transcript) {
        setTranscription(result.transcript);
        setShowTranscription(true); // expande o card so APOS transcrever
      }
    } catch (err) {
      if (err?.limitInfo) {
        setLimitInfo(err.limitInfo);
      } else {
        setTranscriptionError(err.message || 'Falha ao transcrever');
      }
    } finally {
      setIsTranscribing(false);
    }
  };

  // Early return AFTER all hooks
  if (!audioMessage) return null;

  const handlePlayPause = async () => {
    if (isLoading) return;
    
    if (!audioSrc) {
      // Need to load audio first
      setIsLoading(true);
      setError(null);
      
      try {
        // For now, show that we need to download
        // In production, this would call the backend to get base64
        if (onDownload) {
          const result = await onDownload(message.key, 'audio');
          if (result?.base64) {
            const src = `data:${result.mimetype || 'audio/ogg'};base64,${result.base64}`;
            autoPlayRef.current = true; // toca automatico quando o <audio> montar
            setAudioSrc(src);
            setDuration(audioDuration || result.duration || 0);
          } else {
            throw new Error('Não foi possível carregar o áudio');
          }
        } else {
          // Use placeholder duration
          setDuration(audioDuration);
          setError('Áudio não disponível');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 12px',
      // Antes tinha minWidth:200 (piso fixo em px) — no celular o balão é ~65%
      // (~220px) e esse piso empurrava o player para fora da tela. Agora o player
      // se prende à largura do balão (width:100% + minWidth:0) e só limita o teto.
      width: '100%',
      maxWidth: 320,
      minWidth: 0,
      boxSizing: 'border-box',
    },
    speedButton: {
      minWidth: 38,
      height: 26,
      padding: '0 8px',
      borderRadius: 13,
      border: 'none',
      backgroundColor: sentOnDark ? 'rgba(255,255,255,0.18)' : `${colors.accentPrimary}18`,
      color: sentOnDark ? 'rgba(255,255,255,0.95)' : colors.accentPrimary,
      fontSize: 11,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      padding: '0 12px 8px 12px',
    },
    footerLabel: {
      fontSize: 11,
      color: isSent ? colors.messageTimeSent : colors.textTertiary,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      minWidth: 0,
    },
    playButton: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: sentOnDark ? 'rgba(255,255,255,0.2)' : colors.accentPrimary,
      color: sentOnDark ? colors.messageSentText : 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'transform 0.1s ease',
    },
    waveformContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      minWidth: 0,   // deixa o flex-item encolher (senão herda min-content da waveform)
    },
    waveform: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: 28,
      minWidth: 0,
      overflow: 'hidden',   // clipa as barras que não couberem no mobile
    },
    bar: {
      width: 3,
      borderRadius: 2,
      transition: 'background-color 0.1s ease',
    },
    timeContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 11,
      color: isSent ? colors.messageTimeSent : colors.textTertiary,
    },
    pttIndicator: {
      fontSize: 10,
      color: sentOnDark ? 'rgba(255,255,255,0.7)' : colors.textTertiary,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
  };

  const transcribeButtonStyles = {
    link: {
      background: 'transparent',
      border: 'none',
      padding: 0,
      cursor: isTranscribing ? 'not-allowed' : 'pointer',
      fontSize: 11,
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      color: sentOnDark ? 'rgba(255,255,255,0.85)' : colors.accentPrimary,
      whiteSpace: 'nowrap',
      flexShrink: 0,
      opacity: isTranscribing ? 0.7 : 1,
    },
    transcription: {
      marginTop: 8,
      padding: '8px 10px',
      borderRadius: 8,
      backgroundColor: sentOnDark ? 'rgba(255,255,255,0.12)' : colors.bgSecondary,
      color: sentOnDark ? 'rgba(255,255,255,0.95)' : colors.textPrimary,
      fontSize: 13,
      lineHeight: 1.45,
      fontStyle: 'italic',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    transcriptionLabel: {
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      opacity: 0.75,
      marginBottom: 4,
      fontStyle: 'normal',
    },
    errorText: {
      marginTop: 6,
      fontSize: 11,
      color: '#ef4444',
    },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: '100%' }}>
      <div style={styles.container}>
        {audioSrc && <audio ref={audioRef} src={audioSrc} preload="metadata" />}

        {/* Play/Pause Button */}
        <button
          style={styles.playButton}
          onClick={handlePlayPause}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          ) : isPlaying ? (
            <Pause size={20} />
          ) : (
            <Play size={20} style={{ marginLeft: 2 }} />
          )}
        </button>

        {/* Waveform */}
        <div style={styles.waveformContainer}>
          <div style={styles.waveform}>
            {waves.slice(0, 32).map((value, index) => {
              const isPlayed = (index / waves.length) <= progress;
              const barColor = isPlayed
                ? (sentOnDark ? 'rgba(255,255,255,0.9)' : colors.accentPrimary)
                : (sentOnDark ? 'rgba(255,255,255,0.4)' : `${colors.accentPrimary}55`);

              return (
                <div
                  key={index}
                  style={{
                    ...styles.bar,
                    height: `${Math.max(value * 24, 4)}px`,
                    backgroundColor: barColor,
                  }}
                />
              );
            })}
          </div>

          {/* Time */}
          <div style={styles.timeContainer}>
            <span>{formatDuration(audioSrc ? currentTime : 0)}</span>
            <span>{formatDuration(duration || audioDuration)}</span>
          </div>
        </div>

        {/* Velocidade (substitui o baixar — baixar áudio não faz sentido) */}
        <button
          style={styles.speedButton}
          onClick={cycleSpeed}
          title="Velocidade de reprodução"
        >
          {playbackRate}x
        </button>
      </div>

      {/* Rodapé: "Mensagem de voz" à esquerda + Transcrever (estilo label, não
          botão) à direita. A transcrição só expande ao clicar. */}
      <div style={styles.footer}>
        <span style={styles.footerLabel}>
          {error ? (
            <>
              <AlertCircle size={11} color="#ef4444" />
              <span style={{ color: '#ef4444' }}>{error}</span>
            </>
          ) : (
            <>
              <Music size={11} />
              <span>{isPTT ? 'Mensagem de voz' : 'Áudio'}</span>
            </>
          )}
        </span>

        {isTranscribing ? (
          <span style={transcribeButtonStyles.link}>
            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
            Transcrevendo...
          </span>
        ) : transcription ? (
          <button
            type="button"
            style={transcribeButtonStyles.link}
            onClick={() => setShowTranscription((v) => !v)}
          >
            <FileTextIcon size={12} />
            {showTranscription ? 'Ocultar' : 'Transcrição'}
            {showTranscription ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        ) : (
          <button
            type="button"
            style={transcribeButtonStyles.link}
            onClick={handleTranscribe}
            title="Transcrever áudio"
          >
            <FileTextIcon size={12} />
            Transcrever
          </button>
        )}
      </div>

      {/* Texto da transcrição — só quando expandido (mantém o card curto) */}
      {showTranscription && transcription && (
        <div style={{ padding: '0 12px 10px 12px' }}>
          <div style={transcribeButtonStyles.transcription}>
            {transcription}
          </div>
        </div>
      )}
      {transcriptionError && (
        <div style={{ padding: '0 12px 8px 12px' }}>
          <div style={transcribeButtonStyles.errorText}>{transcriptionError}</div>
        </div>
      )}

      <TranscriptionLimitModal
        isOpen={!!limitInfo}
        onClose={() => setLimitInfo(null)}
        currentCount={limitInfo?.current_count || 0}
        maxAllowed={limitInfo?.max_allowed || 0}
        tierName={(limitInfo?.tier || 'sales_free').replace('sales_', '').replace(/^./, (c) => c.toUpperCase())}
        requiredTier={limitInfo?.required_tier || 'sales_pro'}
      />
    </div>
  );
};

// ============================================
// VIDEO MESSAGE COMPONENT
// ============================================

export const VideoMessage = ({
  message,
  isSent,
  onOpenLightbox,
  onDownload,
}) => {
  const { colors } = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);
  const videoMessage = message.message?.videoMessage;

  if (!videoMessage) return null;
  
  const thumbnail = videoMessage.jpegThumbnail;
  const caption = videoMessage.caption;
  const duration = videoMessage.seconds;
  const width = videoMessage.width || 300;
  const height = videoMessage.height || 200;
  
  const aspectRatio = width / height;
  const displayWidth = Math.min(280, width);
  const displayHeight = displayWidth / aspectRatio;
  
  const styles = {
    container: {
      position: 'relative',
      borderRadius: 8,
      overflow: 'hidden',
      cursor: 'pointer',
      maxWidth: 280,
    },
    thumbnail: {
      width: displayWidth,
      height: Math.min(displayHeight, 320),
      objectFit: 'cover',
      display: 'block',
      backgroundColor: colors.bgTertiary,
    },
    placeholder: {
      width: displayWidth,
      height: Math.min(displayHeight, 200),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.3)',
    },
    playButton: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      border: '2px solid white',
    },
    duration: {
      position: 'absolute',
      bottom: 8,
      right: 8,
      backgroundColor: 'rgba(0,0,0,0.6)',
      color: 'white',
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 500,
    },
    downloadBtn: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'rgba(0,0,0,0.6)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      padding: 0,
    },
    caption: {
      padding: '8px 0 0',
      fontSize: 14,
      color: isSent ? colors.messageSentText : colors.messageReceivedText,
      wordBreak: 'break-word',
    },
  };

  const handleClick = () => {
    if (onOpenLightbox) {
      onOpenLightbox({
        type: 'video',
        thumbnail,
        messageKey: message.key,
        caption,
        duration,
      });
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      if (onDownload) {
        const result = await onDownload(message.key, 'video');
        if (result?.base64) {
          // Cross-plataforma (iOS/PWA não honram <a download>).
          await saveBase64File(
            result.base64,
            result.mimetype || 'video/mp4',
            result.fileName || `video_${Date.now()}.mp4`
          );
        }
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div style={styles.container} onClick={handleClick}>
        {thumbnail ? (
          <img
            src={`data:image/jpeg;base64,${thumbnail}`}
            alt={caption || 'Vídeo'}
            style={styles.thumbnail}
          />
        ) : (
          <div style={styles.placeholder}>
            <Video size={48} />
          </div>
        )}
        <button
          style={styles.downloadBtn}
          onClick={handleDownload}
          disabled={isDownloading}
          title="Baixar"
        >
          {isDownloading ? (
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Download size={16} />
          )}
        </button>
        <div style={styles.overlay}>
          <div style={styles.playButton}>
            <Play size={28} style={{ marginLeft: 4 }} />
          </div>
        </div>
        {duration && (
          <div style={styles.duration}>
            {formatDuration(duration)}
          </div>
        )}
      </div>
      {caption && <p style={styles.caption}>{caption}</p>}
    </div>
  );
};

// ============================================
// DOCUMENT MESSAGE COMPONENT
// ============================================

export const DocumentMessage = ({ 
  message, 
  isSent,
  onDownload,
}) => {
  const { colors } = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);
  
  const documentMessage = message.message?.documentMessage;
  if (!documentMessage) return null;
  
  const fileName = documentMessage.fileName || 'Documento';
  const fileSize = documentMessage.fileLength;
  const mimetype = documentMessage.mimetype;
  // Header de preview tipo WhatsApp Web: o WhatsApp ja manda a miniatura da 1a
  // pagina (jpegThumbnail) e o numero de paginas no proprio documentMessage.
  const thumbnail = documentMessage.jpegThumbnail;
  const pageCount = documentMessage.pageCount;

  const { icon: DocIcon, color: iconColor, label } = getDocumentIcon(mimetype, fileName);
  
  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: 12,
      backgroundColor: isSent ? 'rgba(255,255,255,0.1)' : colors.bgTertiary,
      borderRadius: 8,
      minWidth: 220,
      maxWidth: 320,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
    },
    iconContainer: {
      width: 44,
      height: 44,
      borderRadius: 8,
      backgroundColor: `${iconColor}20`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    info: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    fileName: {
      fontSize: 14,
      fontWeight: 500,
      color: isSent ? colors.messageSentText : colors.textPrimary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      color: isSent ? colors.messageTimeSent : colors.textTertiary,
    },
    downloadBtn: {
      width: 36,
      height: 36,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'transparent',
      color: isSent ? colors.messageSentText : colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background-color 0.15s ease',
    },
    previewImg: {
      width: '100%',
      maxWidth: 320,
      maxHeight: 240,
      objectFit: 'cover',
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      display: 'block',
      cursor: 'pointer',
      backgroundColor: colors.bgTertiary,
    },
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (isDownloading) return;
    
    setIsDownloading(true);
    try {
      if (onDownload) {
        const result = await onDownload(message.key, 'document');
        if (result?.base64) {
          // Cross-plataforma: <a download> com data: não funciona no iOS/PWA
          // (documentos "não baixavam" no celular — myCar 08/08).
          await saveBase64File(
            result.base64,
            result.mimetype || 'application/octet-stream',
            result.fileName || fileName
          );
        }
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 320 }}>
      {/* Header de preview (miniatura da 1a pagina) — igual ao WhatsApp Web */}
      {thumbnail && (
        <img
          src={`data:image/jpeg;base64,${thumbnail}`}
          alt={fileName}
          onClick={handleDownload}
          style={styles.previewImg}
        />
      )}
      <div
        style={{
          ...styles.container,
          ...(thumbnail ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : {}),
        }}
        onClick={handleDownload}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isSent
            ? 'rgba(255,255,255,0.15)'
            : colors.bgSecondary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isSent
            ? 'rgba(255,255,255,0.1)'
            : colors.bgTertiary;
        }}
      >
        <div style={styles.iconContainer}>
          <DocIcon size={24} color={iconColor} />
        </div>

        <div style={styles.info}>
          <span style={styles.fileName} title={fileName}>
            {fileName}
          </span>
          <div style={styles.meta}>
            <span>{label}</span>
            {pageCount > 0 && (
              <>
                <span>•</span>
                <span>{pageCount} {pageCount === 1 ? 'página' : 'páginas'}</span>
              </>
            )}
            {fileSize && (
              <>
                <span>•</span>
                <span>{formatFileSize(fileSize)}</span>
              </>
            )}
          </div>
        </div>

        <button
          style={styles.downloadBtn}
          onClick={handleDownload}
          disabled={isDownloading}
          title="Baixar"
        >
          {isDownloading ? (
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Download size={20} />
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================
// MEDIA LIGHTBOX COMPONENT
// ============================================

export const MediaLightbox = ({
  isOpen,
  onClose,
  media,
  onDownload,
}) => {
  const { colors } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [fullMedia, setFullMedia] = useState(null);
  const [error, setError] = useState(null);
  // URL direta falhou (SAS vencido/403): cai para o download normal em vez de
  // deixar o glifo quebrado na tela preta.
  const [directUrlFailed, setDirectUrlFailed] = useState(false);

  useEffect(() => {
    if (isOpen && media) {
      loadFullMedia();
    } else {
      setFullMedia(null);
      setError(null);
      setDirectUrlFailed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, media, directUrlFailed]);

  const loadFullMedia = async () => {
    // URL cheia já embutível (blob com SAS): exibe DIRETO, sem re-baixar via
    // Evolution — o re-download falhava (mensagem fora do store) e o lightbox
    // caía silenciosamente no thumbnail de ~80px ("imagem minúscula").
    if (media?.url && !directUrlFailed) {
      setFullMedia({ src: media.url, mimetype: null, isThumb: false, isDirect: true });
      return;
    }
    if (!media || !onDownload) {
      setError('Mídia não disponível');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await onDownload(media.messageKey, media.type);
      if (result?.base64) {
        setFullMedia({
          src: `data:${result.mimetype || 'image/jpeg'};base64,${result.base64}`,
          mimetype: result.mimetype,
          isThumb: false,
        });
      } else {
        // Use thumbnail as fallback
        if (media.thumbnail) {
          setFullMedia({
            src: `data:image/jpeg;base64,${media.thumbnail}`,
            mimetype: 'image/jpeg',
            isThumb: true,
          });
        } else {
          throw new Error('Não foi possível carregar a mídia');
        }
      }
    } catch (err) {
      setError(err.message);
      // Still show thumbnail if available
      if (media.thumbnail) {
        setFullMedia({
          src: `data:image/jpeg;base64,${media.thumbnail}`,
          mimetype: 'image/jpeg',
          isThumb: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!fullMedia?.src) return;
    const isVideo = media?.type === 'video';
    const ext = isVideo ? 'mp4' : 'jpg';
    const filename = `${isVideo ? 'video' : 'imagem'}_${Date.now()}.${ext}`;

    // URL cross-origin (blob do Azure): o atributo `download` é IGNORADO e o
    // clique NAVEGAVA a SPA para a imagem crua. Baixa como blob local.
    // Cross-plataforma: share sheet no mobile, visualizador no iOS,
    // <a download> no desktop (iOS/PWA não honram o atributo download).
    const src = fullMedia.src;
    if (/^https?:/i.test(src)) {
      await saveUrlFile(src, filename);
      return;
    }
    if (/^data:/i.test(src)) {
      const [, meta, b64] = src.match(/^data:([^;,]*)(?:;base64)?,(.*)$/) || [];
      if (b64) {
        await saveBase64File(b64, meta || 'application/octet-stream', filename);
        return;
      }
    }
    const link = document.createElement('a');
    link.href = src;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.95)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 12,
      padding: '16px 20px',
      color: 'white',
    },
    closeBtn: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'rgba(255,255,255,0.1)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      overflow: 'hidden',
    },
    media: {
      maxWidth: '90vw',
      maxHeight: '85vh',
      objectFit: 'contain',
      borderRadius: 8,
      // Fallback de thumbnail (~80-100px reais): dá um tamanho digno em vez
      // da miniatura minúscula silenciosa.
      ...(fullMedia?.isThumb
        ? { width: 'min(90vw, 480px)', height: 'auto', imageRendering: 'auto' }
        : {}),
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      color: 'white',
    },
    error: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      color: '#ef4444',
    },
    caption: {
      padding: '12px 20px',
      textAlign: 'center',
      color: 'rgba(255,255,255,0.8)',
      fontSize: 14,
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.header}>
        {fullMedia?.src && (
          <button style={styles.closeBtn} onClick={handleDownload} title="Baixar">
            <Download size={20} />
          </button>
        )}
        <button style={styles.closeBtn} onClick={onClose} title="Fechar">
          <X size={24} />
        </button>
      </div>
      
      <div style={styles.content} onClick={(e) => e.stopPropagation()}>
        {isLoading ? (
          <div style={styles.loading}>
            <Loader2 size={48} style={{ animation: 'spin 1s linear infinite' }} />
            <span>Carregando mídia...</span>
          </div>
        ) : error && !fullMedia ? (
          <div style={styles.error}>
            <AlertCircle size={48} />
            <span>{error}</span>
          </div>
        ) : fullMedia ? (
          media.type === 'video' ? (
            <video
              src={fullMedia.src}
              controls
              autoPlay
              style={styles.media}
            />
          ) : (
            <img
              src={fullMedia.src}
              alt=""
              style={styles.media}
              onError={() => {
                // URL direta (blob com SAS) quebrou: refaz pelo download.
                if (fullMedia.isDirect) setDirectUrlFailed(true);
              }}
            />
          )
        ) : null}
      </div>

      {fullMedia?.isThumb && (
        <div style={{ ...styles.caption, color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
          Visualização em baixa resolução — original indisponível
        </div>
      )}
      {media?.caption && (
        <div style={styles.caption}>{media.caption}</div>
      )}
    </div>
  );
};

// ============================================
// STICKER MESSAGE COMPONENT
// ============================================

export const StickerMessage = ({ message, isSent }) => {
  const sticker = message.message?.stickerMessage || {};
  const url = sticker.url || message.mediaUrl;
  const thumbnail = sticker.jpegThumbnail;

  const src = url
    ? url
    : thumbnail
      ? `data:image/jpeg;base64,${thumbnail}`
      : null;

  if (!src) return null;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isSent ? 'flex-end' : 'flex-start',
        alignItems: 'center',
        padding: 0,
        background: 'transparent',
      }}
    >
      <img
        src={src}
        alt="Figurinha"
        draggable={false}
        style={{
          width: 150,
          height: 150,
          objectFit: 'contain',
          background: 'transparent',
          userSelect: 'none',
        }}
        onError={(e) => {
          if (thumbnail && e.currentTarget.src !== `data:image/jpeg;base64,${thumbnail}`) {
            e.currentTarget.src = `data:image/jpeg;base64,${thumbnail}`;
          }
        }}
      />
    </div>
  );
};

// ============================================
// MAIN MESSAGE MEDIA COMPONENT
// ============================================

const MessageMedia = ({
  message,
  isSent,
  onDownload,
}) => {
  const [lightboxMedia, setLightboxMedia] = useState(null);

  const messageContent = message.message || {};

  // Determine media type
  const hasImage = !!messageContent.imageMessage;
  const hasVideo = !!messageContent.videoMessage;
  const hasAudio = !!messageContent.audioMessage;
  const hasDocument = !!messageContent.documentMessage;
  const hasSticker = !!messageContent.stickerMessage;
  
  const handleOpenLightbox = (media) => {
    setLightboxMedia(media);
  };

  const handleCloseLightbox = () => {
    setLightboxMedia(null);
  };

  return (
    <>
      {hasImage && (
        <ImageMessage
          message={message}
          isSent={isSent}
          onOpenLightbox={handleOpenLightbox}
          onDownload={onDownload}
        />
      )}
      
      {hasVideo && (
        <VideoMessage
          message={message}
          isSent={isSent}
          onOpenLightbox={handleOpenLightbox}
          onDownload={onDownload}
        />
      )}
      
      {hasAudio && (
        <AudioMessage
          message={message}
          isSent={isSent}
          onDownload={onDownload}
        />
      )}
      
      {hasDocument && (
        <DocumentMessage
          message={message}
          isSent={isSent}
          onDownload={onDownload}
        />
      )}

      {hasSticker && (
        <StickerMessage
          message={message}
          isSent={isSent}
        />
      )}

      <MediaLightbox
        isOpen={!!lightboxMedia}
        onClose={handleCloseLightbox}
        media={lightboxMedia}
        onDownload={onDownload}
      />
    </>
  );
};

export default MessageMedia;